import React from 'react';
import Head from 'next/head';
import { Config, I18nContext } from '@deporvillage/utils-library-component';
import {
    CriteoOneTag,
    FacebookPixel,
    Optimize,
    OneTrust,
    TrackingLibrary,
    PixelCampaign,
} from '@deporvillage/frontend-common';
import { cdn } from '@deporvillage/picture-component';

const getTrackingConfig = ({
    tracking,
    ebus,
    optimize,
    environment,
}) => ({
    analytics: {
        name: 'Analytics',
        config: {
            analytics: {
                version: tracking?.analytics?.version,
                code: tracking?.analytics?.code,
            },
            ga4: {
                code:
                    ebus?.providers?.ga4?.config &&
                    ebus.providers.ga4.config[environment]?.code,
                como:
                    (ebus?.providers?.ga4?.config &&
                        ebus?.providers?.ga4?.config[environment]?.como) || null,
            },
            adwords: {
                code: tracking?.adwords?.code,
            },
            optimize: {
                enabled: optimize.enabled,
                code: optimize.code,
            },
            tagManager: {},
        },
    },
    deporvillage: {
        name: ebus?.providers?.deporvillage?.name,
        config: ebus?.providers?.deporvillage?.config[environment],
    },
});

const HomeTemplate = ({ children }) => {
    const {
        lang, ebus, environment, oneTrust, tracking, optimize, facebook, pixelCampaign,
        page, store, storeId, country, url, criteo, locale, extension, stores, trustpilot,
        cdn: cdnConfig,
    } = React.useContext(Config);

    const { translate } = React.useContext(I18nContext);

    const title = translate('meta_data.home.title');
    const description = translate('meta_data.home.description');

    const trackingProviders = getTrackingConfig({
        tracking,
        ebus,
        optimize,
        environment,
    });

    return (
        <>
            <Head>
                <title key={'title'}>{title}</title>
                <meta name='description' content={description} />

                <link rel="schema.DC" href="http://purl.org/dc/elements/1.1/" type="text/plain" />
                <link rel="schema.DCTERMS" href="http://purl.org/dc/terms/" type="text/plain" />
                <meta name='DC.title' lang={lang} content={title} />
                <meta name='DC.description' lang={lang} content={description} />
                {/* eslint-disable-next-line react/no-unknown-property */}
                <meta name='DC.identifier' scheme='DCTERMS.URI' content={`https:${url}`} />
                {/* eslint-disable-next-line react/no-unknown-property */}
                <meta name='DC.type' scheme='DCTERMS.DCMIType' content='Text' />
                <meta name='DC.format' content='text/html; charset=utf-8' />
                {/* eslint-disable-next-line react/no-unknown-property */}
                <meta name='DC.language' scheme='ISO639-1' content={lang} />

                <meta property='og:title' content={title} />
                <meta property='og:description' content={description} />
                <meta property='og:locale' content={locale} />
                <meta property='og:site_name' content={`deporvillage${extension}`} />
                <meta property='og:url' content={`https:${url}`} />

                <link rel='canonical' href={`https:${url}`} />

                {
                    stores && stores.map(({ url, lang }) => (
                        <link key={lang} rel='alternate' href={`https:${url}`} hrefLang={lang} />
                    ))
                }

                <meta
                    name='robots'
                    content={country.code !== store.code ? 'noindex,nofollow' : 'index,follow'}
                />
                <script
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{
                        __html: `
                            {   
                                "@context":"http://schema.org",
                                "@type":"Organization",
                                "name":"Deporvillage",
                                "url":"https:${url}",
                                "logo":{
                                    "@type":"ImageObject",
                                    "url":"${cdn({ src: '/static/default/deporvillage.png' }, cdnConfig)}",
                                    "caption":"Deporvillage",
                                    "width":182,
                                    "height":85
                                    },
                                "description":"${translate('meta_data.home.description_json_ld')}",
                                "legalName":"Deporvillage S.L",
                                "sameAs": [
                                    "https://www.instagram.com/deporvillage/",
                                    "https://www.linkedin.com/company/deporvillage/",
                                    "https://www.facebook.com/deporvillage/",
                                    "https://www.strava.com/clubs/610143",
                                    "https://twitter.com/deporvillage",
                                    "${trustpilot.url}",
                                    "https://www.youtube.com/c/deporvillage",
                                    "https://www.crunchbase.com/organization/deporvillage"
                                ],
                                "image":[
                                    "${cdn({ src: '/static/cms/imagen_magatzem.jpg' }, cdnConfig)}",
                                    "${cdn({ src: '/static/cms/imagen_fundadores2.jpg' }, cdnConfig)}",
                                    "${cdn({ src: '/static/cms/imagen_taller.jpg' }, cdnConfig)}"
                                ],
                                "address":
                                    {
                                        "@type":"PostalAddress",
                                        "addressLocality":"Sant Fruitós de Bages",
                                        "postalCode":"08272",
                                        "streetAddress":"C/ Tines, Pol. Ind. el Grau, 32",
                                        "addressRegion":"${translate('states.es.cat')}",
                                        "addressCountry":"${translate('countries.es')}"
                                    },
                                "foundingDate":"2010-02-11"
                            }
                        `,
                    }}
                />
            </Head>

            {
                optimize && (
                    <Optimize code={optimize.code} />
                )
            }

            {
                oneTrust?.enabled && (
                    <OneTrust
                        id={oneTrust.id}
                        lang={lang}
                        script={oneTrust.script}
                    />
                )
            }

            {
                facebook?.enabled && (
                    <FacebookPixel
                        page={page}
                        currency={country.currency_code}
                    />
                )
            }

            {
                criteo?.enabled && (
                    <CriteoOneTag
                        id={criteo.accountId}
                        script={criteo.script}
                        page={page}
                    />
                )
            }

            {
                ebus?.bucket && (
                    <TrackingLibrary
                        providers={trackingProviders}
                        bucket={ebus.bucket[environment]}
                        breadcrumb={[{ name: 'home' }]}
                        storeId={storeId}
                    />
                )
            }

            {
                pixelCampaign?.enabled && (
                    Object.keys(pixelCampaign.page.homePage).map((k) => (
                        <PixelCampaign key={k} category={pixelCampaign.page.homePage[k]} />
                    ))
                )
            }

            {
                children
            }
        </>
    );
};

export default HomeTemplate;
